import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { useStaticCrud } from 'legacy/lut/hooks/useStaticCrud'
import { useCallback, useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { useSafetyTranslation } from 'cng-common/features/translations/hooks'
import {
  BaseTemplate,
  IBaseTemplate,
  MultiSelectDynamic,
} from 'core-components/library/multiSelect'

import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../../../../../configs/translations'
import { setCockpitFilterGlobalPartners } from '../../../../../../model'
import {
  selectCockpitFilterGlobalPartners,
  selectCockpitGlobalPartnerOptions,
} from '../../../../../../model/selectors'
import { NOT_PART_OF_GLOBAL_PARTNER } from '../../../../../../model/types'
import { CockpitFilterLabel } from '../../../cockpitFilterLabel/CockpitFilterLabel'

import styles from './CockpitGlobalPartnerFilter.module.scss'

const KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.globalPartner
const GLOBAL_PARTNER_SORT = [
  { field: 'SortOrder', dir: 'asc' as const },
  { field: 'GlobalPartnerName', dir: 'asc' as const },
]

export const CockpitGlobalPartnerFilter = ({ hideLabel = false }: { hideLabel?: boolean }) => {
  const dispatch = useAppDispatch()
  const t = useSafetyTranslation()
  const selectedGlobalPartners = useSelector(selectCockpitFilterGlobalPartners)
  const globalPartnerOptions = useSelector(selectCockpitGlobalPartnerOptions)

  const options = useMemo(
    () => [
      ...globalPartnerOptions.map((option) => ({ ...option, SortOrder: 0 })),
      {
        GlobalPartnerId: NOT_PART_OF_GLOBAL_PARTNER,
        GlobalPartnerName: t(KEYS.notPartOfGlobalPartner),
        SortOrder: 1,
      },
    ],
    [globalPartnerOptions, t]
  )

  const source = useStaticCrud<(typeof options)[number]>({
    items: options,
    keyField: 'GlobalPartnerId',
    sorting: GLOBAL_PARTNER_SORT,
  })
  const nameByGlobalPartnerId = useMemo(
    () =>
      new Map(
        options.map(({ GlobalPartnerId, GlobalPartnerName }) => [
          GlobalPartnerId,
          GlobalPartnerName,
        ])
      ),
    [options]
  )
  const renderSelectedGlobalPartner = useCallback(
    (props: IBaseTemplate<(typeof options)[number]>) => (
      <BaseTemplate
        {...props}
        className={styles.chip}
        customValueRender={
          nameByGlobalPartnerId.get(props.item.GlobalPartnerId) ?? props.item.GlobalPartnerName
        }
      />
    ),
    [nameByGlobalPartnerId]
  )

  const handleChange = useCallback(
    (values: string[]) => dispatch(setCockpitFilterGlobalPartners(values)),
    [dispatch]
  )

  useEffect(() => {
    const optionIds = new Set(options.map(({ GlobalPartnerId }) => GlobalPartnerId))
    const validSelections = selectedGlobalPartners.filter((id) => optionIds.has(id))

    if (validSelections.length !== selectedGlobalPartners.length) {
      dispatch(setCockpitFilterGlobalPartners(validSelections))
    }
  }, [dispatch, options, selectedGlobalPartners])

  return (
    <div className={styles.root}>
      <CockpitFilterLabel text={KEYS.label} placeholderOnly={hideLabel} />
      <MultiSelectDynamic
        source={source}
        caption="GlobalPartnerName"
        value={selectedGlobalPartners}
        onChange={handleChange}
        placeholder={t(hideLabel ? KEYS.label : KEYS.all)}
        closeOnSelect={false}
        classes={{ inputClasses: { leftSide: styles['chips-line'] } }}
        itemTemplate={renderSelectedGlobalPartner}
      />
    </div>
  )
}
